import _ from 'lodash';
import { ApplicationSectionsList, ApplicationSectionUtils } from '../../../classes/ApplicationSection';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { ApplicationService } from '../../../services/application/application.service';
import { ApplicationState } from '../../../classes/ApplicationState';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { ValidationService } from '../../../services/validation/validation.service';
import { Controller as CoverpathClientController } from '../../../../../coverpath-client/public/js/Controller';
import { AlertIntent } from '../../../../angular/components/shared/alert/alert.component';
import { GiactUtils } from './../../../../../common/js/GiactUtils';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

const UNAUTHORIZED_ROUTE = 'unauthorized';
const POLICY_DETAIL_VIEW_ROUTE = 'account-center-detailed-view';

@Component({
  moduleId: module.id,
  selector: 'navigation',
  templateUrl: './navigation.component.html',
})
export class NavigationComponent implements OnInit {
  currentSection: number;
  appState: ApplicationState;
  isFirstSection: boolean;
  isLastSection: boolean;
  cpFilingType: string;
  controller: CoverpathClientController;
  alertIntent = AlertIntent;
  submitPolicyError: boolean = false;
  altSubmitSignVerbiageEnabled: boolean = false;

  @Output() validationErrorExists = new EventEmitter<any>();

  constructor(
    private applicationService: ApplicationService,
    private navigationService: NavigationService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private amorphicService: AmorphicService
  ) {
    this.appState = applicationService.appState;
    this.controller = amorphicService.controller as CoverpathClientController;
  }

  ngOnInit() {
    this.cpFilingType = this.appState.controller.customer.getCoverpathFilingType();
    this.submitPolicyError = false;
    this.navigationService.activeApplicationSection$.subscribe((activeSection) => {
      this.currentSection = activeSection;
      this.checkIfFirstOrLastSection();
    });
    this.altSubmitSignVerbiageEnabled = this.controller?.altSubmitSignVerbiageEnabled;
  }

  /**
   * sets isFirstSection to true if current section is first
   * sets isLastSection to true if current section is last
   */
  checkIfFirstOrLastSection(): void {
    this.isFirstSection = this.currentSection === 0;
    this.isLastSection =
      this.currentSection === ApplicationSectionUtils.getAllSections(this.cpFilingType, this.appState).length - 1;
  }

  nextSection(): void {
    if (this.appState.canNavigateToNextSection(this.currentSection)) {
      this.navigationService.activeApplicationSection$.next(
        ApplicationSectionUtils.getNextSection(this.currentSection, this.cpFilingType, this.appState)
      );
    } else {
      this.emitValidationErrorInstance(this.currentSection);
    }
  }

  previousSection(): void {
    if (this.appState.canNavigateToPreviousSection(this.currentSection)) {
      this.navigationService.activeApplicationSection$.next(
        ApplicationSectionUtils.getPreviousSection(this.currentSection, this.cpFilingType, this.appState)
      );
    }
  }

  isSubmitEnabled(): boolean {
    const answered = _.keys(this.appState.qsAnswered);

    // Checks that all top questions have been answered
    const unanswered = _.difference(this.appState.topLevelQs, answered);

    return (
      !this.controller.nbDecommissionPost30Days &&
      !this.submitPolicyError &&
      _.isEmpty(unanswered) &&
      (this.amorphicService.controller.customer.emailValidated || this.isAccountVerified()) &&
      !this.isInvalidPaymentInfo()
    );
  }

  private isAccountVerified(): boolean {
    return this.amorphicService.controller.accountCenterController.getController().isAccountVerified;
  }

  async submitFromNavigation() {
    UILoggerSingleton.getInstance().info(
      {
        module: 'NavigationComponent',
        function: 'submitFromNavigation',
        category: UICategory.request,
      },
      'Performance tracking - Customer has clicked Get My Decision'
    );

    const controller = this.amorphicService.controller;
    const application = controller.customer.application;

    // @TODO refactor when HL-21333 is done
    const hasAccess = await controller.checkApplicationAccess(application).catch(() => false);
    if (hasAccess) {
      this.applicationService.loadingSubject.next(true);
      const error = await this.applicationService.submitFromService();
      if (error) {
        this.submitPolicyError = true;
        return;
      }
      await this.applicationService.recordCoverpathActiveApps();
      return this.router.navigate([POLICY_DETAIL_VIEW_ROUTE], {
        queryParams: { applicationNumber: application.applicationNumber },
      });
    }

    return this.router.navigate([UNAUTHORIZED_ROUTE]);
  }

  async teleInterviewerSubmitFromNavigation() {
    await this.applicationService.handleTeleInterviewerSubmit();
  }

  emitValidationErrorInstance(section: number): void {
    this.validationErrorExists.emit(section);
  }

  isTeleSubmitEnabled() {
    return (
      this.appState.isAppCompleteForAgentSubmission() &&
      !this.amorphicService.controller.customer.application.isTeleCMICompleted
    );
  }

  isInvalidPaymentInfo() {
    const primaryPolicy = this.controller.customer.application.primaryPolicy;
    return (
      ValidationService.isAccountRoutingInfoSame(primaryPolicy) ||
      GiactUtils.isInvalidPayment(
        primaryPolicy.isGiactWebDebitValid,
        primaryPolicy.giactWebDebitResponseCode,
        this.controller.mmGiactWebDebitVerificationServiceEnabled
      )
    );
  }

  showNextButton() {
    return !this.showValidateButton();
  }

  disableNextButton() {
    const sectionName = ApplicationSectionUtils.getSectionNameByNumber(
      this.currentSection,
      this.controller.customer.getCoverpathFilingType(),
      this.appState
    );
    const isPaymentInApplication = this.controller.customer.application.primaryPolicy.paymentInApplication;

    return (
      this.isInvalidPaymentInfo() &&
      (sectionName === ApplicationSectionsList.PAYMENT_METHOD || sectionName === 'Payment') &&
      isPaymentInApplication === 'now'
    );
  }

  showValidateButton() {
    const filingType = this.controller.customer.getCoverpathFilingType();
    const sectionName = ApplicationSectionUtils.getSectionNameByNumber(this.currentSection, filingType, this.appState);
    const isPaymentInApplication = this.controller.customer.application.primaryPolicy.paymentInApplication;

    // CA still runs on 2020 metadata, and payment section has a different name
    return (
      (sectionName === ApplicationSectionsList.PAYMENT_METHOD || sectionName === 'Payment') &&
      isPaymentInApplication === 'now' &&
      GiactUtils.isGiactValidating()
    );
  }
}
